import * as React from "react";
import { useEffect } from "react";
import { useController, useWatch } from "react-hook-form";

import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";

import { useAppSelector } from "app/hooks";
import CircularProgress from "components/circular-progress";
import { JOTFORM_URL } from "constants/common";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { useGetIntakeFormsQuery } from "services/onboarding-requests";

type SelectIntakeFormCodeProps = {
  control: any;
  name: string;
  is_active?: boolean;
  required?: boolean;
};

export default function SelectIntakeForm({
  control,
  name,
  required,
  is_active
}: SelectIntakeFormCodeProps) {
  const [clinic] = useWatch({
    control,
    name: ["clinic"]
  });
  const currentClinic = useAppSelector(selectCurrentClinic);

  const activeClinic = clinic || currentClinic;
  const activePractice = clinic?.practice || currentClinic?.practice;

  const {
    field,
    fieldState: { error }
  } = useController({
    name: name,
    control
  });

  const selectValue = field.value.map(
    ({ intake_form }: { intake_form: number }) => intake_form
  );

  const { data: availableForms = [], isLoading } = useGetIntakeFormsQuery(
    {
      practice: activePractice?.id,
      clinic: activeClinic?.id,
      is_active: is_active
    },
    {
      skip: !activePractice?.id || !activeClinic?.id
    }
  );

  // Remove any saved intake_form IDs that are no longer in the available list
  // (e.g. forms that have been archived since the appointment type was last saved)
  useEffect(() => {
    if (isLoading || availableForms.length === 0 || field.value.length === 0) {
      return;
    }

    const availableIds = new Set(availableForms.map((f) => f.id));

    const cleaned = field.value.filter(
      ({ intake_form }: { intake_form: number }) =>
        availableIds.has(intake_form)
    );

    if (cleaned.length !== field.value.length) {
      field.onChange(cleaned);
    }
  }, [availableForms, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const errorMessage = error?.message;

  return (
    <FormControl
      variant="filled"
      required={required}
      error={Boolean(errorMessage)}
    >
      <InputLabel>Select forms</InputLabel>
      <Select
        multiple
        {...field}
        value={selectValue}
        onChange={(event) => {
          const selectedValues = event.target.value as number[];
          const transformedData = selectedValues?.map((value) => ({
            intake_form: value
          }));

          field.onChange(transformedData);
        }}
        input={<OutlinedInput />}
        renderValue={(selected) => `Selected intake forms: ${selected.length}`}
      >
        {isLoading ? (
          <CircularProgress />
        ) : (
          availableForms.map((form) => (
            <MenuItem key={form.id} value={form.id}>
              <Checkbox checked={selectValue.includes(form.id)} />
              <a
                target="_blank"
                rel="noreferrer"
                href={
                  JOTFORM_URL +
                  form.external_id +
                  "?practice_name=" +
                  activePractice?.name
                }
                onClick={(event) => event.stopPropagation()}
                style={{
                  textDecoration: form.is_active ? undefined : "line-through"
                }}
              >
                {form.title}
              </a>
            </MenuItem>
          ))
        )}
      </Select>
      {Boolean(errorMessage) && (
        <FormHelperText error={Boolean(errorMessage)}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
}
